import { Autocomplete } from '@mui/material';
import {
  Control, Controller, FieldValues, Path,
} from 'react-hook-form';
import classNames from 'classnames';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { FiExternalLink } from 'react-icons/fi';
import Tooltip from './Tooltip';

interface CustomAutocompleteProps<T extends FieldValues> {
  id: string;
  options: string[];
  name: Path<T>;
  control: Control<T>;
  label: string;
  labelAbove?: boolean;
  tooltipText?: string;
  freeSolo?: boolean;
  errMsg?: string;
  disabled?: boolean;
  noMatchesText?: string;
  noMatches?: () => void;
}

const CustomAutocomplete = <T extends FieldValues>({
  id,
  name,
  control,
  options,
  label,
  labelAbove,
  tooltipText,
  freeSolo = true,
  errMsg,
  disabled,
  noMatches,
  noMatchesText,
}: CustomAutocompleteProps<T>) => {
  const commonClasses = 'text-text-primary p-3 border border-transparent hover:border-border-secondary';
  const focusClasses = 'focus:ring-1 focus:outline-none focus:ring-primary-500/20  transition-all duration-200';
  const textInputClasses = classNames(
    commonClasses,
    focusClasses,
    'w-full',
    {
      'border-red-500': errMsg,
      'bg-surface-base': !disabled,
      'bg-surface-secondary': disabled,
    },
    'border-1',
    'border-border-tertiary',
    'dark:border-gradient-brand',
    'button-shadow',
    'rounded-3xl',
    'hidden-number-arrows',
    'themed-date-input',
  );

  return (
    <div>
      {labelAbove && (
        <div className="flex items-center w-full">
          <label htmlFor={id} className="text-left font-sm font-medium mb-1">
            {label}
          </label>
          {tooltipText && (
            <Tooltip content={tooltipText}>
              <AiOutlineQuestionCircle className="ml-1" />
            </Tooltip>
          )}
        </div>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            id={id}
            freeSolo={freeSolo}
            disableClearable
            options={options}
            onChange={(event, newValue) => {
              onChange(newValue);
            }}
            onInputChange={(event, newInputValue) => {
              onChange(newInputValue);
            }}
            value={value || ''}
            noOptionsText={
              noMatches ? (
                <div
                  onClick={() => {
                    noMatches();
                  }}
                  className={classNames(
                    'bg-surface-base text-text-secondary px-4 py-2 cursor-pointer rounded-3xl',
                    'hover:bg-primary-500 hover:text-white',
                    'transition-colors duration-200',
                    'flex items-center',
                  )}
                >
                  {noMatchesText || 'No hay coincidencias'}
                  <FiExternalLink className="ml-1 flex-shrink-0" />
                </div>
              ) : (
                noMatchesText || 'No hay coincidencias'
              )
            }
            renderOption={(props, option, { selected }) => (
                <li
                  {...props}
                  className={classNames(
                    'bg-surface-base text-text-primary px-4 py-2 cursor-pointer rounded-3xl',
                    'hover:bg-primary-500 hover:text-white',
                    'transition-colors duration-200',
                    {
                      'bg-primary-500 text-white': selected,
                    },
                  )}
                >
                  {option}
                </li>
            )}
            slotProps={{
              paper: {
                className: 'bg-surface-primary border border-border-primary shadow-lg',
                sx: {
                  backgroundColor: 'rgb(var(--color-bg-secondary))',
                  borderRadius: '24px',
                },
              },
            }}
            renderInput={(params) => (
              <div ref={params.InputProps.ref}>
                <input
                  {...params.inputProps}
                  placeholder={label}
                  className={textInputClasses}
                  onWheel={(e) => e.currentTarget.blur()}
                  disabled={disabled}
                />
              </div>
            )}
          />
        )}
      />
      {errMsg && <p className="text-sm text-red-500 mt-1">{errMsg}</p>}
    </div>
  );
};

export default CustomAutocomplete;
